/**
 * Thin adapters: Questionnaire / QuestionnaireResponse / Observation → app view fields.
 * Scoring metadata lives on Questionnaire (tags, extensions, optional SCORING_CONFIG_JSON).
 * Canonical scored totals for reporting should prefer Observation derived from QuestionnaireResponse when present.
 */

import type {
  Observation,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
  Task,
} from '@medplum/fhirtypes';
import {
  ITEM_WEIGHT_URL,
  SCORING_CONFIG_URL,
  type ScoringConfiguration,
} from '../pages/settings/template-library/scoringTypes';
import { getItemWeight } from '../pages/settings/template-library/utils/scoringUtils';
import {
  getQuestionnaireMaxScore,
  getScoreRanges,
  getTemplateCategory,
} from '../pages/settings/template-library/utils/templateTags';
import type { Assessment, AssessmentDataPoint } from '../pages/clients/data/types';

/** Bot/UI-written total score on QuestionnaireResponse (preferred when Observation not yet created). */
export const QR_TOTAL_SCORE_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/questionnaire-response-total-score';

/** Human-readable interpretation / severity label on QuestionnaireResponse. */
export const QR_INTERPRETATION_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/questionnaire-response-interpretation';

/**
 * Authoritative UI lifecycle state for all QuestionnaireResponse domains.
 * CodeSystem: https://unify.health/fhir/CodeSystem/qr-detailed-status
 * Assessment-allowed codes: 'draft' (in-progress save), 'completed' (submitted).
 * See docs/prd/Questionnaires/PRD_QUESTIONNAIRE_RESPONSE_BASE_FHIR_MAPPING.md
 */
export const QR_DETAILED_STATUS_EXTENSION = 'https://unify.health/fhir/detailed-status';

/** Tag system for assessment administration method on Questionnaire.meta.tag. */
export const ADMINISTRATION_METHOD_TAG_SYSTEM = 'https://unify.health/fhir/administration-method';

/** LOINC → display type + typical max (when not overridden by Questionnaire extensions). */
export const LOINC_ASSESSMENT_SCORE: Record<string, { assessmentType: string; defaultMax: number }> = {
  '44249-1': { assessmentType: 'PHQ-9', defaultMax: 27 },
  '70274-6': { assessmentType: 'GAD-7', defaultMax: 21 },
  // PCL-5: PTSD Checklist for DSM-5 (LOINC 96448-6), 20 items, 0–4 scale, max 80
  '96448-6': { assessmentType: 'PCL-5', defaultMax: 80 },
};

/**
 * Title keyword patterns → LOINC code. Used as a last-resort fallback when
 * the Questionnaire lacks explicit `code` coding. Add new instruments here.
 */
const TITLE_TO_LOINC: Array<{ pattern: RegExp; loinc: string }> = [
  { pattern: /phq/i, loinc: '44249-1' },
  { pattern: /gad/i, loinc: '70274-6' },
  { pattern: /pcl/i, loinc: '96448-6' },
];

export function isMeasuresQuestionnaire(q: Questionnaire | null | undefined): boolean {
  if (!q) return false;
  return getTemplateCategory(q) === 'measures';
}

export function parseScoringConfigurationJson(
  questionnaire: Questionnaire | null | undefined,
): ScoringConfiguration | null {
  const raw = questionnaire?.extension?.find((e) => e.url === SCORING_CONFIG_URL)?.valueString;
  if (!raw?.trim()) return null;
  try {
    return JSON.parse(raw) as ScoringConfiguration;
  } catch {
    return null;
  }
}

/** Short label before parenthesis in Questionnaire.title, e.g. "PHQ-9 (Depression)" → "PHQ-9". */
export function assessmentTypeLabelFromQuestionnaire(questionnaire: Questionnaire | undefined): string {
  const title = questionnaire?.title ?? questionnaire?.name ?? 'Assessment';
  return title.split('(')[0]?.trim() || title;
}

/**
 * Resolve the LOINC code for a scored Questionnaire.
 *
 * Priority:
 *  1. Explicit `Questionnaire.code` entry in the LOINC system (canonical).
 *  2. Title keyword heuristic (fallback for templates without explicit coding).
 */
export function loincCodeForQuestionnaire(questionnaire: Questionnaire | undefined): string | undefined {
  if (!questionnaire) return undefined;
  // 1. Check explicit code in the LOINC system
  const loincSystem = 'http://loinc.org';
  const fromCode = questionnaire.code?.find((c) => c.system === loincSystem && c.code);
  if (fromCode?.code) return fromCode.code;
  // 2. Title heuristic fallback
  const t = questionnaire.title ?? questionnaire.name ?? '';
  for (const { pattern, loinc } of TITLE_TO_LOINC) {
    if (pattern.test(t)) return loinc;
  }
  return undefined;
}

function weightFromAnswerCoding(answer: QuestionnaireResponseItemAnswer): number | undefined {
  const onCoding = answer.valueCoding?.extension?.find((e) => e.url === ITEM_WEIGHT_URL);
  if (onCoding?.valueDecimal != null) return onCoding.valueDecimal;
  return undefined;
}

/**
 * Sum numeric answers on a QuestionnaireResponse.
 * Includes valueInteger/valueDecimal and itemWeight on valueCoding (common for choice measures).
 */
export function sumQuestionnaireResponseItemValues(qr: QuestionnaireResponse): number {
  let sum = 0;
  const walk = (items: typeof qr.item): void => {
    for (const it of items ?? []) {
      for (const ans of it.answer ?? []) {
        if (ans.valueInteger != null) sum += ans.valueInteger;
        else if (ans.valueDecimal != null) sum += ans.valueDecimal;
        else {
          const codingWeight = weightFromAnswerCoding(ans);
          if (codingWeight != null) sum += codingWeight;
        }
      }
      if (it.item) walk(it.item);
    }
  };
  walk(qr.item);
  return sum;
}

function flattenQuestionnaireItems(items: QuestionnaireItem[] | undefined): QuestionnaireItem[] {
  const out: QuestionnaireItem[] = [];
  const walk = (nodes: QuestionnaireItem[] | undefined): void => {
    for (const node of nodes ?? []) {
      out.push(node);
      if (node.item) walk(node.item);
    }
  };
  walk(items);
  return out;
}

/**
 * Score a measures QR by matching valueCoding answers to Questionnaire answerOption weights.
 * Used when totals were not written as Observation / QR total-score extension (e.g. older in-session saves).
 */
export function sumQuestionnaireResponseWeightedScores(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire,
): number | undefined {
  const byLinkId = new Map(
    flattenQuestionnaireItems(questionnaire.item).map((item) => [item.linkId, item]),
  );
  let sum = 0;
  let matched = 0;

  const walk = (items: typeof qr.item): void => {
    for (const it of items ?? []) {
      const qItem = it.linkId ? byLinkId.get(it.linkId) : undefined;
      for (const ans of it.answer ?? []) {
        if (ans.valueInteger != null) {
          sum += ans.valueInteger;
          matched += 1;
          continue;
        }
        if (ans.valueDecimal != null) {
          sum += ans.valueDecimal;
          matched += 1;
          continue;
        }
        const codingWeight = weightFromAnswerCoding(ans);
        if (codingWeight != null) {
          sum += codingWeight;
          matched += 1;
          continue;
        }
        const code = ans.valueCoding?.code;
        if (code && qItem?.answerOption?.length) {
          const optIndex = qItem.answerOption.findIndex((o) => o.valueCoding?.code === code);
          const opt = optIndex >= 0 ? qItem.answerOption[optIndex] : undefined;
          const w = opt
            ? (getItemWeight(opt) ?? opt.valueInteger ?? (optIndex >= 0 ? optIndex : undefined))
            : undefined;
          if (w != null) {
            sum += w;
            matched += 1;
          }
        }
      }
      if (it.item) walk(it.item);
    }
  };
  walk(qr.item);
  return matched > 0 ? sum : undefined;
}

export function resolveMaxScoreForQuestionnaire(questionnaire: Questionnaire | null | undefined): number {
  if (!questionnaire) return 100;
  const fromExt = getScoreRanges(questionnaire).totalMax;
  if (fromExt != null) return fromExt;
  const fromItems = getQuestionnaireMaxScore(questionnaire.item);
  if (fromItems != null) return fromItems;
  const loinc = loincCodeForQuestionnaire(questionnaire);
  if (loinc && LOINC_ASSESSMENT_SCORE[loinc]) return LOINC_ASSESSMENT_SCORE[loinc].defaultMax;
  return 100;
}

export interface ResolvedAssessmentScore {
  score: number;
  source: 'observation' | 'extension' | 'sum';
}

export function resolveAssessmentScore(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | null | undefined,
  observation: Observation | null | undefined,
): ResolvedAssessmentScore {
  const fromItems = questionnaire
    ? sumQuestionnaireResponseWeightedScores(qr, questionnaire)
    : undefined;
  const itemSum = fromItems ?? sumQuestionnaireResponseItemValues(qr);

  /**
   * Prefer Observation / QR total-score extension, but not a failed coding-only
   * score of 0 when item answers clearly sum higher (assessment-scoring-bot used
   * to overwrite totals after summing only valueInteger).
   */
  const trustStoredTotal = (stored: number): boolean =>
    !(stored === 0 && itemSum > 0);

  if (observation?.valueInteger != null && trustStoredTotal(observation.valueInteger)) {
    return { score: observation.valueInteger, source: 'observation' };
  }
  const qv = observation?.valueQuantity?.value;
  if (qv != null && trustStoredTotal(qv)) {
    return { score: qv, source: 'observation' };
  }
  const ext = qr.extension?.find((e) => e.url === QR_TOTAL_SCORE_EXTENSION)?.valueInteger;
  if (ext != null && trustStoredTotal(ext)) {
    return { score: ext, source: 'extension' };
  }
  if (fromItems != null) {
    return { score: fromItems, source: 'sum' };
  }
  return { score: itemSum, source: 'sum' };
}

export function resolveInterpretationLabel(input: {
  qr: QuestionnaireResponse;
  questionnaire: Questionnaire | null | undefined;
  score: number;
  observation: Observation | null | undefined;
}): string | undefined {
  const { qr, questionnaire, score, observation } = input;
  const fromObs =
    observation?.interpretation?.[0]?.coding?.[0]?.display ?? observation?.interpretation?.[0]?.text;
  if (fromObs) return fromObs;
  const fromQrExt = qr.extension?.find((e) => e.url === QR_INTERPRETATION_EXTENSION)?.valueString;
  if (fromQrExt) return fromQrExt;
  const config = parseScoringConfigurationJson(questionnaire);
  const firstOutput = config?.outputs?.[0];
  if (firstOutput && config?.bands?.[firstOutput.id]?.length) {
    for (const b of config.bands[firstOutput.id]) {
      if (score >= b.low && score <= b.high) return b.label;
    }
  }
  const ranges = questionnaire
    ? getScoreRanges(questionnaire)
    : { totalMax: undefined, goodMax: undefined, okMax: undefined };
  if (ranges.goodMax != null && ranges.okMax != null && ranges.goodMax < ranges.okMax) {
    if (score <= ranges.goodMax) return 'Lower range';
    if (score <= ranges.okMax) return 'Moderate range';
    return 'Higher range';
  }
  return undefined;
}

/**
 * Prefer Observation explicitly derived from this QR; else same calendar day + LOINC inferred from Questionnaire.
 */
export function matchObservationForQuestionnaireResponse(
  observations: Observation[],
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | undefined,
): Observation | undefined {
  const qrId = qr.id;
  if (qrId) {
    const derived = observations.find((o) =>
      o.derivedFrom?.some((r) => r.reference === `QuestionnaireResponse/${qrId}`),
    );
    if (derived) return derived;
  }
  const day = (qr.authored ?? '').slice(0, 10);
  if (!day) return undefined;
  const loinc = loincCodeForQuestionnaire(questionnaire);
  if (!loinc) return undefined;
  return observations.find((o) => {
    const code = o.code?.coding?.[0]?.code;
    const eff = (o.effectiveDateTime ?? o.effectivePeriod?.start ?? '').slice(0, 10);
    return code === loinc && eff === day;
  });
}

export function assessmentCoverageKey(assessmentType: string, isoDateYmd: string): string {
  return `${assessmentType.trim()}|${isoDateYmd}`;
}

export function observationToAssessmentView(
  obs: Observation,
  primaryProvider: string,
  episodeId?: string,
): Assessment | null {
  const code = obs.code?.coding?.[0]?.code;
  if (obs.valueInteger == null || !code) return null;
  const meta = LOINC_ASSESSMENT_SCORE[code];
  if (!meta) return null;
  const day = obs.effectiveDateTime?.slice(0, 10) ?? '2026-01-01';
  const sev =
    obs.interpretation?.[0]?.coding?.[0]?.display ?? obs.interpretation?.[0]?.text ?? undefined;
  return {
    id: `obs-${obs.id}`,
    assessmentType: meta.assessmentType,
    date: day,
    time: '',
    provider: primaryProvider,
    score: obs.valueInteger,
    maxScore: meta.defaultMax,
    severity: sev,
    administeredBy: 'System',
    status: 'Completed',
    ...(episodeId ? { episodeId } : {}),
  };
}

/** Merge completed assessments by calendar day for multi-series trend chart. */
/** Resolve QuestionnaireResponse id from a completed assessment Task.output. */
export function getQuestionnaireResponseIdFromTask(task: Task): string | null {
  const output = task.output;
  if (!output?.length) return null;
  const qrOutput = output.find(
    (o) =>
      o.type?.text === 'QuestionnaireResponse' &&
      o.valueReference?.reference?.startsWith('QuestionnaireResponse/'),
  );
  const ref = qrOutput?.valueReference?.reference;
  if (!ref) return null;
  const parts = ref.split('/');
  return parts.length === 2 ? parts[1] : null;
}

export function getQuestionnaireIdFromQuestionnaireResponse(qr: QuestionnaireResponse): string | null {
  const q = qr.questionnaire;
  if (!q || typeof q !== 'string') return null;
  if (q.startsWith('Questionnaire/')) {
    const id = q.slice('Questionnaire/'.length).trim();
    return id || null;
  }
  return null;
}

export function buildAssessmentTrendDataPoints(assessments: Assessment[]): AssessmentDataPoint[] {
  const completed = assessments.filter((a) => a.status === 'Completed' && a.date?.trim());
  const byDate = new Map<string, AssessmentDataPoint>();
  for (const a of completed) {
    const d = a.date.slice(0, 10);
    const prev = byDate.get(d) ?? { date: d, session: d };
    byDate.set(d, { ...prev, [a.assessmentType]: a.score } as unknown as AssessmentDataPoint);
  }
  return [...byDate.values()].sort((x, y) => x.date.localeCompare(y.date));
}

/** Highest maxScore seen per assessment type among completed rows (for % normalization). */
export function maxScoreByAssessmentType(assessments: Assessment[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const a of assessments) {
    if (a.status !== 'Completed' || !a.date?.trim()) continue;
    const scoreMax = a.maxScore;
    const max = scoreMax != null && scoreMax > 0 ? scoreMax : 100;
    const prev = map.get(a.assessmentType);
    if (prev == null || max > prev) map.set(a.assessmentType, max);
  }
  return map;
}

/** Scale raw trend points to 0–100% using each type's max score. */
export function normalizeAssessmentTrendDataPoints(
  points: AssessmentDataPoint[],
  maxByType: Map<string, number>,
): AssessmentDataPoint[] {
  return points.map((point) => {
    const normalized: Record<string, unknown> = { date: point.date, session: point.session };
    for (const key of Object.keys(point)) {
      if (key === 'date' || key === 'session') continue;
      const raw = (point as unknown as Record<string, unknown>)[key];
      const max = maxByType.get(key);
      if (typeof raw === 'number' && max != null && max > 0) {
        normalized[key] = Math.round((raw / max) * 100);
      }
    }
    return normalized as unknown as AssessmentDataPoint;
  });
}

/**
 * Mantine's area tooltip reads `entry.payload[entry.name]`, not `entry.value`.
 * Patch both fields so hover text shows the raw score alongside its normalized percent.
 */
export function enrichAssessmentTrendTooltipPayload(
  label: unknown,
  payload: readonly Record<string, unknown>[] | undefined,
  rawPoints: AssessmentDataPoint[],
  maxByType: Map<string, number>,
): Record<string, unknown>[] | undefined {
  if (!payload) return undefined;
  const firstRow = payload[0]?.payload;
  const dateKey =
    firstRow && typeof firstRow === 'object' && 'date' in firstRow
      ? String((firstRow as Record<string, unknown>).date)
      : String(label ?? '');
  const rawPoint = rawPoints.find((point) => point.date === dateKey) as
    | (AssessmentDataPoint & Record<string, unknown>)
    | undefined;

  return payload.map((entry) => {
    const seriesName = String(entry.name ?? entry.dataKey ?? '');
    const nestedPayload =
      entry.payload && typeof entry.payload === 'object'
        ? (entry.payload as Record<string, unknown>)
        : {};
    const normalizedValue =
      typeof entry.value === 'number'
        ? entry.value
        : typeof nestedPayload[seriesName] === 'number'
          ? nestedPayload[seriesName]
          : undefined;
    const raw = rawPoint?.[seriesName];
    const max = maxByType.get(seriesName);
    const scoreLabel =
      typeof raw === 'number' && max != null && max > 0 && normalizedValue != null
        ? `${raw}/${max} (${normalizedValue}%)`
        : normalizedValue != null
          ? `${normalizedValue}%`
          : undefined;
    if (!scoreLabel) return entry;
    return {
      ...entry,
      value: scoreLabel,
      payload: { ...nestedPayload, [seriesName]: scoreLabel },
    };
  });
}
